/**
 * Overland位置情報APIサービス
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { apiClient } from '@/api/client'
import type {
  AssignPlaceResponse,
  CreateVisitFromLocationRequest,
  CreateVisitFromLocationResponse,
  MyPlace,
  MyPlaceCreateRequest,
  MyPlaceUpdateRequest,
  OverlandLocation,
  OverlandStats,
  OverlandVisit,
  Place,
  PlaceUpdateRequest,
  TimelineNote,
  TimelineNoteCreateRequest,
  TimelineNoteUpdateRequest,
} from '@/api/types/overland'

/**
 * Overland統計情報を取得
 */
export const getOverlandStats = async (): Promise<OverlandStats> => {
  const res = await apiClient.get('/overland/stats')
  return res.data
}

/**
 * 位置データを取得
 * @param date 日付（YYYY-MM-DD形式）
 * @param limit 取得件数（デフォルト500、最大1000）
 */
export const getOverlandLocations = async (date?: string, limit?: number): Promise<OverlandLocation[]> => {
  const params: Record<string, string | number> = {}
  if (date) params.date = date
  if (limit) params.limit = limit
  const res = await apiClient.get('/overland/locations', { params })
  return res.data
}

/**
 * 訪問データを取得
 * @param date 日付（YYYY-MM-DD形式）
 * @param limit 取得件数（デフォルト100、最大1000）
 */
export const getOverlandVisits = async (date?: string, limit = 100, placeId?: number): Promise<OverlandVisit[]> => {
  const params: Record<string, string | number> = { limit }
  if (date) params.date = date
  if (placeId) params.place_id = placeId
  const res = await apiClient.get('/overland/visits', { params })
  return res.data
}

/**
 * 単一の訪問データを取得（部分更新用）
 */
export const getOverlandVisit = async (visitId: number): Promise<OverlandVisit> => {
  const res = await apiClient.get(`/overland/visits/${visitId}`)
  return res.data
}

// =============================================================================
// 場所（Place）API
// =============================================================================

/** Place一覧画面で全件を取るときの件数（バックエンドの上限と揃える） */
const PLACES_FETCH_ALL_LIMIT = 5000

/**
 * 場所一覧を取得
 * @param sort ソート順（visit_count, last_visited, name, distance）
 * @param limit 最大件数
 * @param search 名前で部分一致検索
 * @param latitude 中心緯度（distance検索時に使用）
 * @param longitude 中心経度（distance検索時に使用）
 * @param maxDistanceKm 最大距離（km）
 */
export const getPlaces = async (
  sort = 'visit_count',
  limit = 100,
  search?: string,
  latitude?: number,
  longitude?: number,
  maxDistanceKm?: number,
): Promise<Place[]> => {
  const params: Record<string, string | number> = { sort, limit }
  if (search) {
    params.search = search
  }
  if (latitude !== undefined) {
    params.latitude = latitude
  }
  if (longitude !== undefined) {
    params.longitude = longitude
  }
  if (maxDistanceKm !== undefined) {
    params.max_distance_km = maxDistanceKm
  }
  const res = await apiClient.get('/overland/places', { params })
  return res.data
}

/**
 * Place一覧画面用：Visit が紐付く Place を全件取得
 * （スタンプラリー等で作られただけの Place は含めない）
 * @param sort ソート順
 */
export const getVisitedPlaces = async (sort = 'visit_count'): Promise<Place[]> => {
  const res = await apiClient.get('/overland/places', {
    params: { sort, limit: PLACES_FETCH_ALL_LIMIT, has_visits: true },
  })
  return res.data
}

/**
 * 場所を更新
 * @param placeId 場所ID
 * @param data 更新データ
 */
export const updatePlace = async (placeId: number, data: PlaceUpdateRequest): Promise<Place> => {
  const params = new URLSearchParams()
  if (data.custom_name !== undefined) {
    params.append('custom_name', data.custom_name ?? '')
  }
  if (data.radius !== undefined) {
    params.append('radius', data.radius.toString())
  }
  const res = await apiClient.patch(`/overland/places/${placeId}?${params.toString()}`)
  return res.data
}

/**
 * 場所を削除
 * @param placeId 場所ID
 */
export const deletePlace = async (placeId: number): Promise<void> => {
  await apiClient.delete(`/overland/places/${placeId}`)
}

/**
 * 複数の場所を一括削除
 * @param placeIds 場所IDの配列
 */
export const deletePlacesBulk = async (
  placeIds: number[],
): Promise<{ message: string; deleted: number; deleted_images: number }> => {
  const res = await apiClient.post('/overland/places/bulk-delete', { place_ids: placeIds })
  return res.data
}

/**
 * 全Placeの訪問カウントを再計算
 */
export const recalcPlaceCounts = async (): Promise<{ message: string; total: number; updated: number }> => {
  const res = await apiClient.post('/overland/places/recalc-counts')
  return res.data
}

/**
 * PlaceをGoogle APIで再ジオコーディング
 * Yahooで取得した場所をGoogleで再取得する
 * @param placeId 場所ID
 * @param googlePlaceId Google Place ID（指定時はそのPlaceで更新）
 * @param displayName 表示名（指定時はGoogle APIの名前より優先）
 */
export const reGeocodePlace = async (placeId: number, googlePlaceId?: string, displayName?: string): Promise<Place> => {
  const params = new URLSearchParams()
  if (googlePlaceId) params.set('google_place_id', googlePlaceId)
  if (displayName) params.set('display_name', displayName)
  const query = params.toString()
  const res = await apiClient.post(`/overland/places/${placeId}/re-geocode${query ? `?${query}` : ''}`)
  return res.data
}

/**
 * 時間範囲内のlocationのmotionを一括更新
 * @param startTime 開始時刻 (ISO format)
 * @param endTime 終了時刻 (ISO format)
 * @param motion 新しい移動手段
 */
export const updateLocationsMotion = async (
  startTime: string,
  endTime: string,
  motion: string,
): Promise<{ message: string; count: number }> => {
  const params = new URLSearchParams({
    start_time: startTime,
    end_time: endTime,
    motion,
  })
  const res = await apiClient.patch(`/overland/locations/motion?${params.toString()}`)
  return res.data
}

/**
 * 訪問データを削除
 * @param visitId 訪問ID
 */
export const deleteVisit = async (visitId: number): Promise<{ message: string; id: number }> => {
  const res = await apiClient.delete(`/overland/visits/${visitId}`)
  return res.data
}

/**
 * VisitからPlaceの紐付けを解除
 * @param visitId 訪問ID
 */
export const unassignPlaceFromVisit = async (
  visitId: number,
): Promise<{ message: string; visit_id: number; old_place_id: number }> => {
  const res = await apiClient.delete(`/overland/visits/${visitId}/place`)
  return res.data
}

export type MergeVisitsResult = {
  merged_visit_id: number
  deleted_visit_ids: number[]
  message: string
}

/**
 * 複数の訪問データをマージ
 * @param visitIds マージする訪問IDの配列（2つ以上必要）
 */
export const mergeVisits = async (visitIds: number[]): Promise<MergeVisitsResult> => {
  const res = await apiClient.post('/overland/visits/merge', { visit_ids: visitIds })
  return res.data
}

// =============================================================================
// マイプレイス（My Places）API
// =============================================================================

/**
 * マイプレイス一覧を取得
 */
export const getMyPlaces = async (): Promise<MyPlace[]> => {
  const res = await apiClient.get('/overland/my-places')
  return res.data
}

/**
 * マイプレイスを作成
 */
export const createMyPlace = async (data: MyPlaceCreateRequest): Promise<MyPlace> => {
  const res = await apiClient.post('/overland/my-places', data)
  return res.data
}

/**
 * マイプレイスを更新
 */
export const updateMyPlace = async (placeId: number, data: MyPlaceUpdateRequest): Promise<MyPlace> => {
  const res = await apiClient.patch(`/overland/my-places/${placeId}`, data)
  return res.data
}

/**
 * マイプレイスを削除
 */
export const deleteMyPlace = async (placeId: number): Promise<void> => {
  await apiClient.delete(`/overland/my-places/${placeId}`)
}

/**
 * 訪問に場所を割り当てる
 */
export const assignPlaceToVisit = async (visitId: number, placeId: number): Promise<AssignPlaceResponse> => {
  const res = await apiClient.put(`/overland/visits/${visitId}/place?place_id=${placeId}`)
  return res.data
}

/**
 * 訪問情報を更新する（出発時刻など）
 */
export type UpdateVisitRequest = {
  arrival_date?: string // ISO8601形式
  departure_date?: string | null // ISO8601形式、nullで滞在中に戻す
}

export type UpdateVisitResponse = {
  id: number
  arrival_date: string | null
  departure_date: string | null
  message: string
}

export const updateVisit = async (visitId: number, data: UpdateVisitRequest): Promise<UpdateVisitResponse> => {
  const params = new URLSearchParams()
  if (data.arrival_date !== undefined) {
    params.append('arrival_date', data.arrival_date)
  }
  if (data.departure_date !== undefined) {
    params.append('departure_date', data.departure_date ?? '')
  }
  const res = await apiClient.patch(`/overland/visits/${visitId}?${params.toString()}`)
  return res.data
}

// =============================================================================
// Visit手動作成 API
// =============================================================================

/**
 * 移動ポイント（Location）からVisitを手動作成
 * departure_dateは周辺Locationから自動推定される
 */
export const createVisitFromLocation = async (
  data: CreateVisitFromLocationRequest,
): Promise<CreateVisitFromLocationResponse> => {
  const res = await apiClient.post('/overland/visits/create-from-location', data)
  return res.data
}

// =============================================================================
// 場所検索 API
// =============================================================================

export type NearbyPlaceResult = {
  name: string
  address: string | null
  latitude: number
  longitude: number
  category: string | null
  distance_m: number | null
  google_place_id?: string | null
  /** 行ったことがある場所なら 1 以上（Google 検索のみ） */
  visit_count?: number
  last_visited_at?: string | null
}

/**
 * Yahoo APIで近隣の場所を検索
 * @param latitude 中心緯度
 * @param longitude 中心経度
 * @param keyword キーワード（オプション）
 * @param radius 検索半径（km）
 * @param limit 最大件数
 */
export const searchNearbyPlaces = async (
  latitude: number,
  longitude: number,
  keyword?: string,
  radius = 0.5,
  limit = 10,
): Promise<NearbyPlaceResult[]> => {
  const params: Record<string, string | number> = { latitude, longitude, radius, limit }
  if (keyword) params.keyword = keyword
  const res = await apiClient.get('/overland/places/search/nearby', { params })
  return res.data
}

/**
 * 検索結果からPlaceを作成してVisitに紐付け
 */
export type CreatePlaceFromSearchResult = {
  id: number
  name: string
  address?: string | null
  google_maps_url?: string | null
  website_url?: string | null
  images: Array<{
    id: number
    url: string
  }>
  is_new: boolean
  message: string
}

export const createPlaceFromSearch = async (
  visitId: number,
  searchResult: NearbyPlaceResult,
): Promise<CreatePlaceFromSearchResult> => {
  const res = await apiClient.post('/overland/places/create-from-search', {
    visit_id: visitId,
    name: searchResult.name,
    address: searchResult.address,
    latitude: searchResult.latitude,
    longitude: searchResult.longitude,
    category: searchResult.category,
    google_place_id: searchResult.google_place_id,
  })
  return res.data
}

/**
 * Google Places API検索
 * - キーワードあり: テキスト検索（観光地やランドマークも検索可能）
 * - キーワードなし: 周辺検索（近くの場所を検索）
 */
export const searchPlacesGoogle = async (
  latitude: number,
  longitude: number,
  keyword?: string,
  radius = 5.0,
  limit = 10,
): Promise<NearbyPlaceResult[]> => {
  const params: Record<string, string | number> = { latitude, longitude, radius, limit }
  if (keyword?.trim()) {
    params.keyword = keyword.trim()
  }
  const res = await apiClient.get('/overland/places/search/google', { params })
  return res.data
}

// =============================================================================
// Place Image API
// =============================================================================

/** Add image by file upload */
export const addPlaceImageFile = async (placeId: number, files: File[]): Promise<void> => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  await apiClient.post(`/overland/places/${placeId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/** Add image by URL */
export const addPlaceImageUrl = async (placeId: number, url: string): Promise<void> => {
  await apiClient.post(`/overland/places/${placeId}/images/url`, { url })
}

/** Reorder images */
export const reorderPlaceImages = async (placeId: number, imageIds: number[]): Promise<void> => {
  await apiClient.patch(`/overland/places/${placeId}/images/order`, { image_ids: imageIds })
}

/** Delete a single place image */
export const deletePlaceImage = async (placeId: number, imageId: number): Promise<void> => {
  await apiClient.delete(`/overland/places/${placeId}/images/${imageId}`)
}

// =============================================================================
// Timeline Notes API
// =============================================================================

/**
 * タイムラインメモ一覧を取得
 * @param date 日付（YYYY-MM-DD形式）
 */
export const getTimelineNotes = async (date?: string): Promise<TimelineNote[]> => {
  const params: Record<string, string> = {}
  if (date) params.date = date
  const res = await apiClient.get('/overland/timeline-notes', { params })
  return res.data
}

/**
 * タイムラインメモを作成
 */
export const createTimelineNote = async (data: TimelineNoteCreateRequest): Promise<TimelineNote> => {
  const res = await apiClient.post('/overland/timeline-notes', data)
  return res.data
}

/**
 * タイムラインメモを更新
 */
export const updateTimelineNote = async (noteId: number, data: TimelineNoteUpdateRequest): Promise<TimelineNote> => {
  const res = await apiClient.patch(`/overland/timeline-notes/${noteId}`, data)
  return res.data
}

/**
 * タイムラインメモを削除
 */
export const deleteTimelineNote = async (noteId: number): Promise<void> => {
  await apiClient.delete(`/overland/timeline-notes/${noteId}`)
}
